import { PropertyFilterProps } from "@amzn/awsui-components-react/polaris/property-filter";
import { PropertyFilterOperatorExtended } from "@amzn/awsui-collection-hooks";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import DatePicker from "@amzn/awsui-components-react/polaris/date-picker";
import DateInput from "@amzn/awsui-components-react/polaris/date-input";
import Calendar from "@amzn/awsui-components-react/polaris/calendar";
import { Document } from "models/schemas";
import { matchDateFilter } from "utils/table-filtering-utils";

export type AtlasDocumentFilteringProperty =
  PropertyFilterProps.FilteringProperty & {
    key:
      | keyof Document
      | "fileName"
      | "parsingStatus"
      | "modifiedBy"
      | "modifiedAt";
    getValueFormatter?: (valueOptions: any) => any;
    propertyValues?: string[];
  };

export const pageSizePreferenceAtlasKB = {
  title: "Page size",
  options: [
    { value: 50, label: "50 items" },
    { value: 100, label: "100 items" },
    { value: 200, label: "200 items" },
  ],
};

export const createAtlasFilterProperties = (
  flatDocs: Document[],
): AtlasDocumentFilteringProperty[] => [
  {
    propertyLabel: "Name",
    key: "fileName",
    groupValuesLabel: "Name",
    operators: [":", "=", "^", "!:", "!=", "!^"],
  },
  {
    propertyLabel: "Size",
    key: "size",
    groupValuesLabel: "Size values",
    operators: [":", "=", "^", "!:", "!=", "!^"],
  },
  {
    propertyLabel: "Uploaded On",
    key: "modifiedAt",
    groupValuesLabel: "Uploaded on values",
    operators: (["=", "!=", "<", "<=", ">", ">="] as const).map(
      (operator): PropertyFilterOperatorExtended<string> => ({
        operator,
        form: ({ value, onChange, filter }: any) => {
          const handleDateChange = (dateString: string) => {
            if (dateString) {
              const date = new Date(dateString);
              onChange(date.toISOString().split("T")[0]);
            } else {
              onChange("");
            }
          };
          if (typeof filter === "undefined") {
            return (
              <FormField>
                <DatePicker
                  value={value ?? ""}
                  onChange={(event) => handleDateChange(event.detail.value)}
                  placeholder="YYYY/MM/DD"
                  locale="en-GB"
                />
              </FormField>
            );
          }
          return (
            <div className="date-form">
              <FormField>
                <DateInput
                  value={value ?? ""}
                  onChange={(event) => handleDateChange(event.detail.value)}
                  placeholder="YYYY/MM/DD"
                />
              </FormField>
              <Calendar
                value={value ?? ""}
                onChange={(event) => handleDateChange(event.detail.value)}
                locale="en-GB"
              />
            </div>
          );
        },
        match: (itemValue: unknown, filterValue: string) =>
          matchDateFilter(itemValue as string, filterValue, operator),
      }),
    ),
  },
  {
    propertyLabel: "Uploaded By",
    key: "modifiedBy",
    groupValuesLabel: "Uploaded by values",
    operators: [":", "=", "^", "!:", "!=", "!^"],
    propertyValues: Array.from(
      new Set(flatDocs.map((doc) => (doc as any).modifiedBy).filter(Boolean)),
    ).sort(),
  },
  {
    propertyLabel: "Parsing Status",
    key: "parsingStatus",
    groupValuesLabel: "Parsing status values",
    operators: [
      { operator: "=", tokenType: "enum" as const },
      { operator: "!=", tokenType: "enum" as const },
    ],
    propertyValues: Array.from(
      new Set(
        flatDocs.map((doc) => (doc as any).parsingStatus).filter(Boolean),
      ),
    ).sort(),
  },
];
